import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { Message } from '@ipfabric/ui-components/Message/Message';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import type { LabelTargetType, SnapshotId } from '@ipfabric/utils';
import { OlderSnapshotAssignmentNotice } from 'pages/Settings/DiscoverySnapshots/DiscoveryDataEnrichment/AssignedLabels/OlderSnapshotAssignmentNotice';
import { ReflectToRulesSwitch } from 'pages/Settings/DiscoverySnapshots/DiscoveryDataEnrichment/AssignedLabels/ReflectToRulesSwitch';
import { assignmentSourceLinkText } from 'pages/Settings/DiscoverySnapshots/DiscoveryDataEnrichment/AssignedLabels/assignedByColumn';
import { useIsLatestSnapshot } from 'pages/Settings/DiscoverySnapshots/DiscoveryDataEnrichment/AssignedLabels/useIsLatestSnapshot';
import { LabelPicker } from 'pages/Settings/DiscoverySnapshots/DiscoveryDataEnrichment/shared/LabelPicker/LabelPicker';
import { toLabelDataTestIdSuffix } from 'pages/Settings/DiscoverySnapshots/DiscoveryDataEnrichment/shared/toLabelDataTestIdSuffix';
import { useOpenDiscoveryDataEnrichment } from 'pages/Settings/DiscoverySnapshots/DiscoveryDataEnrichment/useOpenDiscoveryDataEnrichment';
import { useState } from 'react';
import { useLabelAssignments } from './useLabelAssignments';

type LabelsEditorProps = {
  onClose: () => void;
  onSaved: () => void;
  snapshotId: SnapshotId;
  targetId: string;
  targetType: LabelTargetType;
  title: string;
};

const sourceLabel = (
  source: 'manual' | 'auto' | 'inherited' | 'ipf',
): string => {
  switch (source) {
    case 'manual':
      return 'Manual';
    case 'auto':
      return 'Auto assigned';
    case 'inherited':
      return 'Inherited';
    case 'ipf':
      return 'IP Fabric';
  }
};

export const LabelsEditor = ({
  onClose,
  onSaved,
  snapshotId,
  targetId,
  targetType,
  title,
}: LabelsEditorProps) => {
  const openDiscoveryDataEnrichment = useOpenDiscoveryDataEnrichment();
  const isLatestSnapshot = useIsLatestSnapshot(snapshotId);
  const [view, setView] = useState<'main' | 'labels'>('main');
  const [pickerDraftLabels, setPickerDraftLabels] = useState<string[]>([]);
  const [reflectToRules, setReflectToRules] = useState(isLatestSnapshot);
  const {
    applyPickerDraft,
    draftManual,
    draftManualLabels,
    isSubmitting,
    loading,
    readOnlyLabels,
    removeManualLabel,
    save,
  } = useLabelAssignments({
    onSaved,
    snapshotId,
    targetId,
    targetType,
  });

  const openLabelPicker = () => {
    setPickerDraftLabels(draftManualLabels);
    setView('labels');
  };

  const handleApplyPickerDraft = () => {
    applyPickerDraft(pickerDraftLabels);
    setView('main');
  };

  return (
    <SidePanel
      closeOnBackdropClick={false}
      footer={
        view === 'labels' ? (
          <div className='flex gap-xs'>
            <Button
              className='flex-1'
              dataTestId='labels-picker-add'
              onClick={handleApplyPickerDraft}
              type='button'
              variant='primary'
            >
              {pickerDraftLabels.length > 0
                ? `Add ${pickerDraftLabels.length} selected labels`
                : 'Add selected labels'}
            </Button>
            <Button
              className='flex-1'
              dataTestId='labels-picker-back'
              onClick={() => setView('main')}
              type='button'
              variant='outline'
            >
              Back
            </Button>
          </div>
        ) : undefined
      }
      onClose={onClose}
      title={view === 'labels' ? 'Label selection' : `Labels | ${title}`}
    >
      {view === 'main' ? (
        <div className='flex flex-col gap-sm'>
          {!isLatestSnapshot ? <OlderSnapshotAssignmentNotice /> : null}
          <Card className='flex flex-col gap p'>
            <span className='text-sm font-semibold'>
              Inherited or Auto-assigned label
            </span>
            <Message
              message='Inherited and Auto-assigned labels can’t be edited from here'
              type='info'
              variant='plain'
            />
            {loading ? (
              <span className='text-sm'>Loading labels…</span>
            ) : (
              <div className='flex flex-col gap-xs'>
                {readOnlyLabels.map((item) => {
                  const { assignedByLink } = item;

                  return (
                    <div
                      className='grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-xs'
                      key={item.labelId}
                    >
                      <span className='min-w-0 text-sm'>{item.label}</span>
                      <span className='text-sm text-disabled'>
                        {sourceLabel(item.source)}
                      </span>
                      {assignedByLink && openDiscoveryDataEnrichment ? (
                        <Link
                          icon='fa-angle-right'
                          onClick={() =>
                            openDiscoveryDataEnrichment(assignedByLink)
                          }
                          renderAs='button'
                          type='button'
                        >
                          {assignmentSourceLinkText(assignedByLink)}
                        </Link>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
          <Card className='flex flex-col gap p'>
            <span className='text-sm font-semibold'>Manual labels</span>
            {loading ? (
              <span className='text-sm'>Loading labels…</span>
            ) : (
              <>
                <div className='flex flex-col gap-xs'>
                  <div className='flex items-center justify-between gap-sm'>
                    <span className='text-sm font-semibold'>Labels</span>
                    <Button
                      dataTestId='labels-add-new'
                      icon='fa-plus-circle'
                      iconPosition='start'
                      onClick={openLabelPicker}
                      type='button'
                      variant='plain'
                    >
                      Add label
                    </Button>
                  </div>
                  {draftManual.length === 0 ? null : (
                    <div className='flex flex-col gap-xs'>
                      {draftManual.map((item) => (
                        <div
                          className='flex w-full items-center gap-sm'
                          key={item.labelId}
                        >
                          <span className='min-w-0 flex-1 text-sm'>
                            {item.label}
                          </span>
                          <TableButton
                            accent='negative'
                            dataTestId={`labels-remove-${toLabelDataTestIdSuffix(item.label)}`}
                            icon='fa-trash-can'
                            onClick={() => removeManualLabel(item.label)}
                            type='button'
                            variant='plain'
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <ReflectToRulesSwitch
                  checked={reflectToRules}
                  onChange={setReflectToRules}
                />
                <Button
                  accent='positive'
                  className='w-full'
                  dataTestId='labels-save'
                  icon='fa-save'
                  loading={isSubmitting}
                  onClick={() => void save(reflectToRules)}
                  type='button'
                  variant='primary'
                >
                  Save manual labels
                </Button>
              </>
            )}
          </Card>
        </div>
      ) : (
        <LabelPicker
          onChange={setPickerDraftLabels}
          selectedLabels={pickerDraftLabels}
        />
      )}
    </SidePanel>
  );
};
